import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Gift, MapPin, ScrollText, UserRound, Sparkles, Pencil, Save, X, Trophy, Share2, MessageCircle, Facebook, Copy, Star, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import ContestMedia from "@/components/ContestMedia";
import ScreenMedia from "@/components/ScreenMedia";
import { isLightHex } from "@/lib/theme";
import { isCustomHost } from "@/lib/host";
import { TextImage, Video, Faq, Countdown, Gallery, ButtonBlock } from "@/components/landing/Blocks";
import CustomHtml from "@/components/landing/CustomHtml";

export { isCustomHost };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

function Editable({ edit, value, onChange, as: Tag = "p", className, testId, multiline }) {
  if (!edit) return <Tag className={className} data-testid={testId}>{value}</Tag>;
  return (
    <Tag contentEditable suppressContentEditableWarning data-testid={`${testId}-edit`}
      onBlur={(e) => onChange(multiline ? e.currentTarget.innerText : e.currentTarget.textContent)}
      className={`${className} outline-dashed outline-2 outline-amber-400/80 rounded px-1 cursor-text focus:outline-amber-300 focus:bg-white/5`}>{value}</Tag>
  );
}

function EditBar({ dirty, saving, onSave, onExit }) {
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-slate-900/95 border border-amber-400/50 px-4 py-2 shadow-2xl text-sm" data-testid="landing-edit-bar">
      <Pencil className="h-4 w-4 text-amber-400" /> <span className="text-white/90">Modalità modifica: clicca un testo per cambiarlo</span>
      <button type="button" onClick={onSave} disabled={!dirty || saving} className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-400 text-slate-900 font-semibold px-3 py-1 disabled:opacity-40" data-testid="landing-edit-save"><Save className="h-3.5 w-3.5" /> {saving ? "Salvo…" : "Salva"}</button>
      <button type="button" onClick={onExit} className="inline-flex items-center gap-1 rounded-full bg-white/10 text-white px-3 py-1" data-testid="landing-edit-exit"><X className="h-3.5 w-3.5" /> Esci</button>
    </div>
  );
}

function Hero({ d, L, edit, set, customer, onCta, onLogin }) {
  const navigate = useNavigate();
  const logo = fileUrl(d.logo_file_id) || d.logo_url;
  const ended = d.status === "Concluso";
  return (
    <header className="relative overflow-hidden lp-dark" data-testid="landing-hero">
      <div className="absolute inset-0 opacity-40">{L.hero_file_id ? <img src={fileUrl(L.hero_file_id)} alt="" className="w-full h-full object-cover" data-testid="landing-hero-image" /> : <ScreenMedia screens={d.game?.screens} phase="idle" format="landscape" fit="cover" />}</div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-[var(--lp-bg)]" />
      <div className="relative max-w-5xl mx-auto px-6 pt-10 pb-16 sm:pt-16 sm:pb-24 text-white">
        {logo && <img src={logo} alt={d.name} className="h-16 sm:h-24 object-contain mb-6 drop-shadow-lg" data-testid="landing-logo" />}
        <span className={`inline-block text-xs font-semibold rounded-full px-3 py-1 mb-4 ${ended ? "bg-white/20" : "bg-emerald-500/90"}`} data-testid="landing-status">
          {ended ? "Concorso concluso" : d.status === "Programmato" ? `Dal ${fmtDate(d.start_datetime)}` : `Attivo fino al ${fmtDate(d.end_datetime)}`}
        </span>
        <Editable edit={edit} value={L.hero_title} onChange={(v) => set("hero_title", v)} as="h1" testId="landing-title" className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight drop-shadow-lg max-w-3xl" />
        <Editable edit={edit} value={L.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} testId="landing-subtitle" multiline className="mt-4 text-base md:text-lg text-white/90 max-w-2xl whitespace-pre-line" />
        <div className="mt-8 flex flex-wrap gap-3">
          {!ended && (
            <button type="button" onClick={onCta} className="inline-flex items-center gap-2 rounded-full bg-[var(--lp-accent)] text-slate-900 font-bold px-6 py-3 text-base shadow-xl hover:scale-[1.03] transition-transform" data-testid="landing-cta">
              <Sparkles className="h-5 w-5" /> <Editable edit={edit} value={L.cta_label} onChange={(v) => set("cta_label", v)} as="span" testId="landing-cta-label" />
            </button>
          )}
          <button type="button" onClick={onLogin} className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/30 px-6 py-3 text-base font-semibold hover:bg-white/25 transition-colors" data-testid="landing-login">
            <UserRound className="h-5 w-5" /> {customer ? "I miei premi" : "Registrati / Accedi"}
          </button>
          {!ended && d.receipt_ocr_enabled && (
            <button type="button" onClick={() => { if (!edit) navigate(`/scontrino/${d.id}`); }} className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/30 px-6 py-3 text-base font-semibold hover:bg-white/25 transition-colors" data-testid="landing-receipt-cta">
              <Camera className="h-5 w-5" /> Partecipa con lo scontrino
            </button>
          )}
          <a href="#regolamento" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-base font-semibold text-white/80 hover:text-white" data-testid="landing-rules-link"><ScrollText className="h-5 w-5" /> Regolamento</a>
        </div>
      </div>
    </header>
  );
}

function Prizes({ prizes }) {
  if (!prizes?.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-6 py-12" data-testid="landing-prizes">
      <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-[var(--lp-accent)] flex items-center gap-2"><Trophy className="h-5 w-5" /> I premi in palio</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {prizes.map((p, i) => (
          <div key={`${p.name}-${i}`} className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden backdrop-blur" data-testid={`landing-prize-${i}`}>
            {p.image_url ? <img src={p.image_url} alt={p.name} className="h-40 w-full object-cover" /> : <div className="h-24 grid place-items-center bg-white/5"><Gift className="h-10 w-10 text-[var(--lp-accent)]" /></div>}
            <div className="p-4"><p className="font-semibold text-white">{p.name}</p>{p.value && <p className="text-sm text-white/70">{p.value}</p>}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Rules({ d }) {
  const text = d.rules_text || d.rules_summary;
  return (
    <section id="regolamento" className="max-w-5xl mx-auto px-6 py-12" data-testid="landing-rules">
      <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-[var(--lp-accent)] flex items-center gap-2"><ScrollText className="h-5 w-5" /> Regolamento</h2>
      {text && <p className="mt-4 text-white/80 whitespace-pre-line text-sm leading-relaxed max-w-3xl">{text}</p>}
      <a href={`/regolamento/${d.id}`} className="mt-4 inline-block underline text-white/90 text-sm" data-testid="landing-rules-full">Leggi il regolamento completo</a>
      {(d.privacy_text || d.privacy_file_id) && <a href={`/privacy/${d.id}`} className="mt-4 ml-4 inline-block underline text-white/90 text-sm" data-testid="landing-privacy-link">Informativa privacy</a>}
    </section>
  );
}

function Share({ d, customer, onLogin }) {
  const [mine, setMine] = useState(null);
  const L = d.landing || {};
  useEffect(() => {
    if (customer) api.myRef(d.id).then(setMine).catch(() => {});
  }, [customer, d.id]); // eslint-disable-line react-hooks/exhaustive-deps -- api è un modulo stabile
  const url = mine?.url || `${window.location.origin}/c/${L.slug}?src=share`;
  const msg = (tpl, fallback) => (tpl || fallback).replace(/\{LINK\}/g, url);
  const waText = msg(L.share_whatsapp, `Partecipa a ${d.name}: {LINK}`);
  const fbQuote = msg(L.share_facebook, `Partecipa a ${d.name}!`).replace(url, "").trim();
  const track = () => api.landingEvent(d.id, "share");
  const copy = async () => {
    track();
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else { const ta = document.createElement("textarea"); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
      toast.success("Link copiato");
    } catch { window.prompt("Copia il link:", url); }
  };
  const referral = L.referral_enabled;
  return (
    <section className="max-w-5xl mx-auto px-6 py-10" data-testid="landing-share">
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-[var(--lp-accent)] flex items-center gap-2"><Share2 className="h-5 w-5" /> Invita un amico</h2>
            <p className="text-sm text-white/75 mt-1">{referral ? <>Porta un amico: quando partecipa dal tuo link ricevi <b className="text-white">{L.referral_description}</b>.</> : "Condividi il concorso con chi vuoi: più siete, più ci si diverte."}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer" onClick={track} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-slate-900 font-semibold px-4 py-2 text-sm hover:brightness-110" data-testid="landing-share-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(fbQuote)}`} target="_blank" rel="noreferrer" onClick={track} className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] text-white font-semibold px-4 py-2 text-sm hover:brightness-110" data-testid="landing-share-facebook"><Facebook className="h-4 w-4" /> Facebook</a>
            <button type="button" onClick={copy} className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-2 text-sm font-semibold hover:bg-white/25" data-testid="landing-share-copy"><Copy className="h-4 w-4" /> Copia link</button>
          </div>
        </div>
        {referral && (customer ? (
          <div className="rounded-xl bg-black/20 border border-white/10 p-3 text-sm" data-testid="landing-ref-box">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-white/60 text-xs uppercase tracking-wider">Il tuo link personale</p>
              {mine && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/70" data-testid="landing-ref-friends">Amici portati: <b className="text-white">{mine.friends_total}</b></span>
                  {mine.ambassador && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--lp-accent)] text-slate-900 text-[11px] font-bold px-2.5 py-1" data-testid="landing-ambassador-badge"><Star className="h-3 w-3 fill-current" /> Ambasciatore Vivi Win</span>}
                </div>
              )}
            </div>
            <p className="font-mono-code break-all text-white/90 mt-1" data-testid="landing-ref-url">{mine ? mine.url : "…"}</p>
            {mine?.rewards?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="landing-ref-rewards">
                <p className="text-white/60 text-xs uppercase tracking-wider">I tuoi premi invito</p>
                {mine.rewards.map((r) => <p key={r.code} className="flex flex-wrap gap-2 items-center"><span className="font-mono-code font-bold text-[var(--lp-accent)]">{r.code}</span><span className="text-white/80">{r.description}</span><span className={`text-xs ${r.redeemed ? "text-white/50" : "text-emerald-300"}`}>{r.redeemed ? "usato" : `valido fino al ${fmtDate(r.expires_at)}`}</span></p>)}
              </div>
            )}
          </div>
        ) : (
          <button type="button" onClick={onLogin} className="text-sm underline text-white/85 hover:text-white" data-testid="landing-ref-login">Accedi con la tua email per ottenere il link personale e ricevere il premio invito</button>
        ))}
      </div>
    </section>
  );
}

export default function Landing({ hostMode = false }) {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const edit = params.get("edit") === "1" && isAdmin;

  useEffect(() => {
    api.landingResolve(hostMode ? "" : slug, hostMode ? window.location.hostname : "").then((r) => {
      setD(r);
      const ref = (params.get("ref") || "").trim().toUpperCase();
      if (ref) localStorage.setItem("vw_ref", JSON.stringify({ code: ref, ts: Date.now(), contest: r.id }));
      if (params.get("edit") !== "1") api.landingEvent(r.id, "view", params.get("src") || "", ref || undefined);
    }).catch((e) => setErr(e?.response?.data?.detail || "Pagina non trovata"));
    api.adminMe().then(() => setIsAdmin(true)).catch(() => {});
    api.customerMe().then(setCustomer).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, hostMode]); // eslint-disable-line react-hooks/exhaustive-deps -- ricarica solo al cambio di landing, non a ogni setter

  const L = useMemo(() => ({ ...(d?.landing || {}), ...draft }), [d, draft]);
  const set = useCallback((k, v) => setDraft((x) => ({ ...x, [k]: v })), []);
  const dirty = Object.keys(draft).length > 0;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.updateLanding(d.id, draft);
      setD(r); setDraft({});
      toast.success("Landing salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-slate-950 text-white p-6 text-center" data-testid="landing-error"><div><p className="text-2xl font-bold">Pagina non trovata</p><p className="text-white/60 mt-2">{err}</p></div></div>;
  if (!d) return <div className="min-h-screen bg-slate-950" />;
  const rawBg = (d.game?.bg_color || "").toLowerCase();
  const bg = !rawBg || rawBg === "#0f172a" ? "#ffffff" : rawBg;
  const accent = d.game?.accent_color || "#2f6fe4";
  const lpLight = isLightHex(bg);
  const onCta = () => {
    if (edit) return;
    api.landingEvent(d.id, "cta");
    if (d.channel === "whatsapp" && d.whatsapp_number && !d.web_enabled) window.location.href = `https://wa.me/${d.whatsapp_number.replace(/\D/g, "")}?text=${encodeURIComponent("Ciao! Voglio partecipare a " + d.name)}`;
    else navigate(d.web_enabled ? `/online/${d.id}` : `/gioca/${d.id}`);
  };
  const loginPath = hostMode ? "/accedi" : `/c/${L.slug}/accedi`;
  const onLogin = () => {
    api.landingEvent(d.id, "login");
    if (customer) return navigate("/i-miei-premi");
    navigate(loginPath);
  };

  if (L.layout_mode === "html" && L.custom_html) {
    return (
      <div className="min-h-screen" style={{ background: bg }} data-testid="landing-page">
        <CustomHtml d={d} html={L.custom_html} loginPath={loginPath} ctaPath={d.web_enabled ? `/online/${d.id}` : `/gioca/${d.id}`} />
        {isAdmin && <a href={`/concorso/${d.id}?tab=landing`} className="fixed bottom-4 right-4 z-[70] inline-flex items-center gap-2 rounded-full bg-amber-400 text-slate-900 font-semibold px-4 py-2 shadow-xl" data-testid="landing-edit-enter"><Pencil className="h-4 w-4" /> Modifica HTML nel pannello</a>}
      </div>
    );
  }

  const blocks = (L.blocks?.length ? L.blocks : [{ id: "hero", type: "hero" }, { id: "media", type: "media" }, { id: "how_to", type: "how_to" }, { id: "stores", type: "stores" }, { id: "prizes", type: "prizes" }, { id: "share", type: "share" }, { id: "rules", type: "rules" }]).filter((b) => b.enabled !== false);
  const ended = d.status === "Concluso";
  const render = (b) => {
    switch (b.type) {
      case "hero": return <Hero key={b.id} d={d} L={L} edit={edit} set={set} customer={customer} onCta={onCta} onLogin={onLogin} />;
      case "media": return <section key={b.id} className="max-w-5xl mx-auto px-6 -mt-6 sm:-mt-10 relative"><ContestMedia contest={d} /></section>;
      case "how_to": return (
        <section key={b.id} className="max-w-5xl mx-auto px-6 py-12" data-testid="landing-how">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-widest text-[var(--lp-accent)] flex items-center gap-2"><Sparkles className="h-5 w-5" /> Come partecipare</h2>
          <Editable edit={edit} value={L.how_to} onChange={(v) => set("how_to", v)} testId="landing-howto" multiline className="mt-4 text-white/85 whitespace-pre-line leading-relaxed max-w-3xl" />
        </section>);
      case "stores": return L.show_stores !== false && d.stores?.length > 0 ? (
        <section key={b.id} className="max-w-5xl mx-auto px-6 py-6"><div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="landing-stores">
          <h3 className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--lp-accent)]" /> Punti vendita aderenti</h3>
          <ul className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm text-white/80">{d.stores.map((s) => <li key={s.id}>• {s.name}</li>)}</ul></div></section>) : null;
      case "prizes": return L.show_prizes !== false ? <Prizes key={b.id} prizes={d.prizes} /> : null;
      case "share": return !ended ? <Share key={b.id} d={{ ...d, landing: L }} customer={customer} onLogin={onLogin} /> : null;
      case "rules": return <Rules key={b.id} d={d} />;
      case "text_image": return <TextImage key={b.id} b={b} />;
      case "video": return <Video key={b.id} b={b} />;
      case "faq": return <Faq key={b.id} b={b} />;
      case "countdown": return !ended ? <Countdown key={b.id} b={b} d={d} /> : null;
      case "gallery": return <Gallery key={b.id} b={b} />;
      case "button": return <ButtonBlock key={b.id} b={b} onCta={onCta} />;
      default: return null;
    }
  };

  return (
    <div className={`min-h-screen text-white ${lpLight ? "lp-light" : "lp-dark"}`} style={{ "--lp-bg": bg, "--lp-accent": accent, background: bg }} data-testid="landing-page">
      <main>{blocks.map(render)}</main>
      <footer className="border-t border-white/10 mt-8 py-8 text-center text-xs text-white/60 px-6" data-testid="landing-footer">
        <Editable edit={edit} value={L.footer || `${d.name} · Concorso a premi valido dal ${fmtDate(d.start_datetime)} al ${fmtDate(d.end_datetime)}. Vivi Win`} onChange={(v) => set("footer", v)} testId="landing-footer-text" multiline className="whitespace-pre-line" />
        <p className="mt-2"><a href={`/privacy/${d.id}`} className="underline" data-testid="landing-footer-privacy">Privacy</a></p>
      </footer>
      {isAdmin && !edit && <button type="button" onClick={() => setParams({ edit: "1" })} className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 rounded-full bg-amber-400 text-slate-900 font-semibold px-4 py-2 shadow-xl" data-testid="landing-edit-enter"><Pencil className="h-4 w-4" /> Modifica pagina</button>}
      {edit && <EditBar dirty={dirty} saving={saving} onSave={save} onExit={() => { setDraft({}); setParams({}); }} />}
    </div>
  );
}
